import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2, Search, Building2, Users, Phone, Mail, MapPin, FileText, X, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stable per-row id factory for editable add/remove lists. Plain monotonic
// counter — sufficient because these arrays live for the duration of a single
// open dialog and never collide with persisted ids (backend never sees `_uid`).
// Why: React's reconciliation needs a key that's stable across re-orderings.
// Index-as-key breaks when the user removes a row mid-edit (focus leaks into
// the next row, IME composition state can shift, etc.). See React docs:
// https://react.dev/learn/rendering-lists#why-does-react-need-keys
let __rowUidCounter = 0;
const newUid = () => `r${++__rowUidCounter}`;
const withUid = (v) => (
  typeof v === "object" && v !== null
    ? { _uid: newUid(), ...v }
    : { _uid: newUid(), value: v ?? "" }
);
const stripUid = ({ _uid: _, ...rest }) => rest;

const CustomerDatabase = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Add/Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Form state. Editable list rows carry a stable `_uid` key — stripped on
  // save. `contact_persons` already holds objects, so the uid is added inline.
  // `contact_numbers` and `email_addresses` are wrapped as `{_uid, value}`
  // tuples for the same reason; they're flattened back to plain strings in
  // `handleSubmit`.
  const [formData, setFormData] = useState({
    company_name: "",
    contact_persons: [withUid({ name: "", role: "" })],
    contact_numbers: [withUid("")],
    office_address: "",
    factory_address: "",
    gstin: "",
    email_addresses: [withUid("")]
  });
  
  // Delete confirmation
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/wo-customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
    ];
    
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/wo-customers/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(
        `Import successful! ${response.data.imported} new customers added, ${response.data.updated} updated.`
      );
      fetchCustomers();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: "",
      contact_persons: [withUid({ name: "", role: "" })],
      contact_numbers: [withUid("")],
      office_address: "",
      factory_address: "",
      gstin: "",
      email_addresses: [withUid("")]
    });
    setEditingCustomer(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      company_name: customer.company_name || "",
      contact_persons: customer.contact_persons?.length > 0
        ? customer.contact_persons.map((cp) => withUid(cp))
        : [withUid({ name: "", role: "" })],
      contact_numbers: customer.contact_numbers?.length > 0
        ? customer.contact_numbers.map((n) => withUid(n))
        : [withUid("")],
      office_address: customer.office_address || "",
      factory_address: customer.factory_address || "",
      gstin: customer.gstin || "",
      email_addresses: customer.email_addresses?.length > 0
        ? customer.email_addresses.map((e) => withUid(e))
        : [withUid("")]
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.company_name.trim()) {
      toast.error("Company Name is required");
      return;
    }

    // Strip the client-only `_uid` keys before sending to the backend, and
    // flatten the `{_uid, value}` string-tuple shape back to plain strings.
    const cleanedData = {
      company_name: formData.company_name.trim(),
      contact_persons: formData.contact_persons
        .map(stripUid)
        .filter(cp => cp.name && cp.name.trim()),
      contact_numbers: formData.contact_numbers
        .map(n => (n.value || "").trim())
        .filter(Boolean),
      office_address: formData.office_address.trim(),
      factory_address: formData.factory_address.trim(),
      gstin: formData.gstin.trim(),
      email_addresses: formData.email_addresses
        .map(e => (e.value || "").trim())
        .filter(Boolean)
    };

    try {
      if (editingCustomer) {
        await axios.put(`${API}/wo-customers/${editingCustomer.id}`, cleanedData);
        toast.success("Customer updated successfully!");
      } else {
        await axios.post(`${API}/wo-customers`, cleanedData);
        toast.success("Customer added successfully!");
      }
      setShowDialog(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error(error.response?.data?.detail || "Failed to save customer");
    }
  };

  const handleDelete = async () => {
    if (!deleteCustomerId) return;

    try {
      await axios.delete(`${API}/wo-customers/${deleteCustomerId}`);
      toast.success("Customer deleted successfully!");
      setDeleteCustomerId(null);
      fetchCustomers();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    }
  };

  // Contact Person handlers
  const addContactPerson = () => {
    setFormData(prev => ({
      ...prev,
      contact_persons: [...prev.contact_persons, withUid({ name: "", role: "" })]
    }));
  };

  const removeContactPerson = (index) => {
    if (formData.contact_persons.length > 1) {
      setFormData(prev => ({
        ...prev,
        contact_persons: prev.contact_persons.filter((_, i) => i !== index)
      }));
    }
  };

  const updateContactPerson = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      contact_persons: prev.contact_persons.map((cp, i) => 
        i === index ? { ...cp, [field]: value } : cp
      )
    }));
  };

  // Contact Number handlers
  const addContactNumber = () => {
    setFormData(prev => ({
      ...prev,
      contact_numbers: [...prev.contact_numbers, withUid("")]
    }));
  };

  const removeContactNumber = (index) => {
    if (formData.contact_numbers.length > 1) {
      setFormData(prev => ({
        ...prev,
        contact_numbers: prev.contact_numbers.filter((_, i) => i !== index)
      }));
    }
  };

  const updateContactNumber = (index, value) => {
    setFormData(prev => ({
      ...prev,
      contact_numbers: prev.contact_numbers.map((num, i) => 
        i === index ? { ...num, value } : num
      )
    }));
  };

  // Email handlers
  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      email_addresses: [...prev.email_addresses, withUid("")]
    }));
  };

  const removeEmail = (index) => {
    if (formData.email_addresses.length > 1) {
      setFormData(prev => ({
        ...prev,
        email_addresses: prev.email_addresses.filter((_, i) => i !== index)
      }));
    }
  };

  const updateEmail = (index, value) => {
    setFormData(prev => ({
      ...prev,
      email_addresses: prev.email_addresses.map((email, i) => 
        i === index ? { ...email, value } : email
      )
    }));
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer =>
    customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.gstin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.contact_persons?.some(cp => cp.name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
            Customer Database
          </h1>
          <p className="text-gray-400 mt-1">Manage customer details for work orders</p>
        </div>
        <div className="flex gap-3">
          {/* Upload Excel Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
          >
            {uploading ? (
              <>
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-emerald-400 border-t-transparent rounded-full" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Excel
              </>
            )}
          </Button>
          
          <Button 
            onClick={openAddDialog}
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-black font-bold"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Excel Format Info */}
      <Card className="bg-emerald-900/20 border-emerald-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="h-5 w-5 text-emerald-400 mt-0.5" />
            <div className="text-sm">
              <p className="text-emerald-400 font-semibold">Excel Upload Format</p>
              <p className="text-gray-400 mt-1">
                Columns: <span className="text-white">Company Name</span>, <span className="text-white">Contact Person</span>, <span className="text-white">Role</span>, <span className="text-white">Contact Number</span>, <span className="text-white">Email</span>, <span className="text-white">GSTIN</span>, <span className="text-white">Office Address</span>, <span className="text-white">Factory Address</span>
              </p>
              <p className="text-gray-500 mt-1 text-xs">Multiple contacts/phones/emails can be in separate rows with the same company name - they will be merged automatically.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="bg-black/60 border-amber-500/30">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by company name, GSTIN, or contact person..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900/80 border-gray-700 text-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer Table */}
      <Card className="bg-black/60 border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-amber-400 flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Customers ({filteredCustomers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading customers...</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {searchTerm ? "No customers found matching your search" : "No customers yet. Add your first customer!"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-amber-400">Company Name</TableHead>
                    <TableHead className="text-amber-400">Contact Person(s)</TableHead>
                    <TableHead className="text-amber-400">Contact Number(s)</TableHead>
                    <TableHead className="text-amber-400">Email(s)</TableHead>
                    <TableHead className="text-amber-400">GSTIN</TableHead>
                    <TableHead className="text-amber-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} className="border-gray-700 hover:bg-amber-500/10">
                      <TableCell className="font-semibold text-white">
                        {customer.company_name}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {customer.contact_persons?.map((cp, i) => (
                          <div key={i} className="text-sm">
                            {cp.name} {cp.role && <span className="text-gray-500">({cp.role})</span>}
                          </div>
                        )) || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {customer.contact_numbers?.map((num, i) => (
                          <div key={i} className="text-sm">{num}</div>
                        )) || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {customer.email_addresses?.map((email, i) => (
                          <div key={i} className="text-sm">{email}</div>
                        )) || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300 font-mono text-sm">
                        {customer.gstin || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(customer)}
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteCustomerId(customer.id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {editingCustomer ? "Edit Customer" : "Add New Customer"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Company Name */}
            <div>
              <Label className="text-amber-300 font-semibold">Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Enter company name"
                className="bg-gray-800 border-gray-600 text-white mt-1"
              />
            </div>

            {/* Contact Persons */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-amber-300 font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Contact Person(s)
                </Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addContactPerson}
                  className="bg-amber-600 hover:bg-amber-700 text-black font-bold"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.contact_persons.map((cp, index) => (
                  <div key={cp._uid} className="flex gap-2 items-center">
                    <Input
                      value={cp.name}
                      onChange={(e) => updateContactPerson(index, "name", e.target.value)}
                      placeholder="Contact name"
                      className="bg-gray-800 border-gray-600 text-white flex-1"
                    />
                    <Input
                      value={cp.role}
                      onChange={(e) => updateContactPerson(index, "role", e.target.value)}
                      placeholder="Role (e.g., Manager)"
                      className="bg-gray-800 border-gray-600 text-white w-40"
                    />
                    {formData.contact_persons.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeContactPerson(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Numbers */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-amber-300 font-semibold flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Contact Number(s)
                </Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addContactNumber}
                  className="bg-amber-600 hover:bg-amber-700 text-black font-bold"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.contact_numbers.map((num, index) => (
                  <div key={num._uid} className="flex gap-2 items-center">
                    <Input
                      value={num.value}
                      onChange={(e) => updateContactNumber(index, e.target.value)}
                      placeholder="Phone number"
                      className="bg-gray-800 border-gray-600 text-white flex-1"
                    />
                    {formData.contact_numbers.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeContactNumber(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Email Addresses */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-amber-300 font-semibold flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address(es)
                </Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addEmail}
                  className="bg-amber-600 hover:bg-amber-700 text-black font-bold"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.email_addresses.map((email, index) => (
                  <div key={email._uid} className="flex gap-2 items-center">
                    <Input
                      type="email"
                      value={email.value}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder="Email address"
                      className="bg-gray-800 border-gray-600 text-white flex-1"
                    />
                    {formData.email_addresses.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeEmail(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* GSTIN */}
            <div>
              <Label className="text-amber-300 font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4" />
                GSTIN
              </Label>
              <Input
                value={formData.gstin}
                onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                placeholder="Enter GSTIN (e.g., 29AABCU9603R1ZM)"
                className="bg-gray-800 border-gray-600 text-white mt-1 font-mono"
                maxLength={15}
              />
            </div>

            {/* Office Address */}
            <div>
              <Label className="text-amber-300 font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Office Address
              </Label>
              <Textarea
                value={formData.office_address}
                onChange={(e) => setFormData(prev => ({ ...prev, office_address: e.target.value }))}
                placeholder="Enter office address"
                className="bg-gray-800 border-gray-600 text-white mt-1 min-h-[80px]"
              />
            </div>

            {/* Factory Address */}
            <div>
              <Label className="text-amber-300 font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Factory Address
              </Label>
              <Textarea
                value={formData.factory_address}
                onChange={(e) => setFormData(prev => ({ ...prev, factory_address: e.target.value }))}
                placeholder="Enter factory address"
                className="bg-gray-800 border-gray-600 text-white mt-1 min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-black font-bold"
            >
              {editingCustomer ? "Update Customer" : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCustomerId} onOpenChange={() => setDeleteCustomerId(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Customer</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete this customer? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 text-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerDatabase;
